// ───────────────────────────────────────────────────────────────────────────
// Görsel İnspektör — Analiz Motoru
// Bir <iframe> içinde gerçekten render edilmiş sayfanın DOM'u üzerinde çalışır.
// Hiçbir harici bağımlılık yok; saf DOM + CSSOM ölçümü.
// ───────────────────────────────────────────────────────────────────────────

export interface ImageInfo {
  page: string;
  src: string;
  domain: string;
  alt: string | null;        // null = alt attribute hiç yok
  hasAltAttr: boolean;
  decorative: boolean;       // alt="" (bilinçli boş)
  naturalW: number;
  naturalH: number;
  dispW: number;
  dispH: number;
  broken: boolean;
  lazy: boolean;
  bytes: number;             // 0 = bilinmiyor
  oversized: boolean;        // doğal boyut ekrandakinin >2x'i
  oversizeFactor: number;
  isBackground: boolean;
}

export interface ContrastIssue {
  page: string;
  sample: string;
  fg: string;
  bg: string;
  ratio: number;
  fontPx: number;
  bold: boolean;
  required: number;
  level: "fail" | "pass";
}

export interface A11yIssue {
  page: string;
  severity: "error" | "warning";
  rule: string;
  message: string;
  detail?: string;
}

export interface SeoResult {
  page: string;
  path: string;
  title: string;
  titleLen: number;
  description: string;
  descLen: number;
  hasViewport: boolean;
  lang: string;
  canonical: string;
  h1Count: number;
  ogTitle: string;
  ogImage: string;
  ogDesc: string;
  favicon: boolean;
}

export interface TypographyResult {
  fonts: string[];
  sizes: number[];
  weights: number[];
  headingOutline: { tag: string; text: string; bad: boolean }[];
}

export interface PageAudit {
  page: string;
  path: string;
  images: ImageInfo[];
  contrast: ContrastIssue[];
  a11y: A11yIssue[];
  seo: SeoResult;
  typography: TypographyResult;
  overflowX: boolean;
  scrollW: number;
  clientW: number;
  domNodes: number;
}

// ── Renk yardımcıları (WCAG 2.1) ───────────────────────────────────────────

export function parseColor(str: string): [number, number, number, number] | null {
  if (!str) return null;
  const m = str.match(/rgba?\(([^)]+)\)/);
  if (!m) return null;
  const parts = m[1].split(",").map((s) => parseFloat(s.trim()));
  if (parts.length < 3) return null;
  return [parts[0], parts[1], parts[2], parts.length >= 4 ? parts[3] : 1];
}

function channelLum(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

export function relLuminance(rgb: [number, number, number]): number {
  return 0.2126 * channelLum(rgb[0]) + 0.7152 * channelLum(rgb[1]) + 0.0722 * channelLum(rgb[2]);
}

export function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

export function rgbToHex(rgb: [number, number, number]): string {
  return (
    "#" +
    rgb
      .map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase()
  );
}

// İki rengi alfaya göre harmanla (üst rengin alt renk üzerine düşmesi)
function blend(top: [number, number, number, number], bottom: [number, number, number]): [number, number, number] {
  const a = top[3];
  return [
    top[0] * a + bottom[0] * (1 - a),
    top[1] * a + bottom[1] * (1 - a),
    top[2] * a + bottom[2] * (1 - a),
  ];
}

// Bir elemanın efektif (opak) arka plan rengini ataları gezerek bulur.
// opaque=false → görsel/degrade üstünde, güvenilir kontrast hesaplanamaz.
function effectiveBg(
  el: Element,
  win: Window
): { rgb: [number, number, number]; opaque: boolean } {
  let node: Element | null = el;
  let acc: [number, number, number, number][] = [];
  while (node && node.nodeType === 1) {
    const cs = win.getComputedStyle(node);
    // Arka planda görsel varsa kontrast hesaplanamaz
    if (cs.backgroundImage && cs.backgroundImage !== "none") {
      return { rgb: [255, 255, 255], opaque: false };
    }
    const c = parseColor(cs.backgroundColor);
    if (c && c[3] > 0) {
      acc.push(c);
      if (c[3] >= 0.999) {
        // Opak zemin bulundu; üstteki yarı saydamları harmanla
        let base: [number, number, number] = [c[0], c[1], c[2]];
        for (let i = acc.length - 2; i >= 0; i--) base = blend(acc[i], base);
        return { rgb: base, opaque: true };
      }
    }
    node = node.parentElement;
  }
  // Zincirde opak zemin yok → beyaz varsay (açık tema)
  let base: [number, number, number] = [255, 255, 255];
  for (let i = acc.length - 1; i >= 0; i--) base = blend(acc[i], base);
  return { rgb: base, opaque: acc.length === 0 ? true : false };
}

function resolveUrl(src: string, base: string): string {
  try {
    return new URL(src, base).href;
  } catch {
    return src;
  }
}

function domainOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "—";
  }
}

// ── Görsel denetimi ─────────────────────────────────────────────────────────

function auditImages(doc: Document, win: Window, page: string): ImageInfo[] {
  const out: ImageInfo[] = [];
  const dpr = win.devicePixelRatio || 1;
  const base = doc.baseURI || win.location.href;

  // <img> elemanları
  const imgs = Array.from(doc.querySelectorAll("img")) as HTMLImageElement[];
  for (const img of imgs) {
    try {
      const rect = img.getBoundingClientRect();
      const src = resolveUrl(img.currentSrc || img.src || "", base);
      const naturalW = img.naturalWidth || 0;
      const naturalH = img.naturalHeight || 0;
      const dispW = Math.round(rect.width);
      const dispH = Math.round(rect.height);
      const broken = img.complete && naturalW === 0;
      const hasAltAttr = img.hasAttribute("alt");
      const alt = hasAltAttr ? img.getAttribute("alt") : null;
      const decorative = hasAltAttr && (alt || "").trim() === "";
      const oversizeFactor =
        dispW > 0 && naturalW > 0 ? naturalW / (dispW * dpr) : 0;
      out.push({
        page,
        src,
        domain: domainOf(src),
        alt,
        hasAltAttr,
        decorative,
        naturalW,
        naturalH,
        dispW,
        dispH,
        broken,
        lazy: img.loading === "lazy",
        bytes: 0,
        oversized: !broken && oversizeFactor > 2 && dispW > 0,
        oversizeFactor,
        isBackground: false,
      });
    } catch {
      /* tek görsel hatası tüm denetimi bozmasın */
    }
  }

  // CSS background-image
  const all = Array.from(doc.querySelectorAll("*")) as HTMLElement[];
  const seen = new Set<string>();
  for (const el of all) {
    try {
      const cs = win.getComputedStyle(el);
      const bi = cs.backgroundImage;
      if (!bi || bi === "none" || !bi.includes("url(")) continue;
      const m = bi.match(/url\(["']?([^"')]+)["']?\)/);
      if (!m) continue;
      const url = resolveUrl(m[1], base);
      if (url.startsWith("data:")) continue;
      const rect = el.getBoundingClientRect();
      const key = url + "@" + Math.round(rect.width) + "x" + Math.round(rect.height);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        page,
        src: url,
        domain: domainOf(url),
        alt: null,
        hasAltAttr: true, // dekoratif sayılır (CSS bg)
        decorative: true,
        naturalW: 0,
        naturalH: 0,
        dispW: Math.round(rect.width),
        dispH: Math.round(rect.height),
        broken: false,
        lazy: false,
        bytes: 0,
        oversized: false,
        oversizeFactor: 0,
        isBackground: true,
      });
    } catch {
      /* yoksay */
    }
  }

  return out;
}

// ── Kontrast denetimi ───────────────────────────────────────────────────────

function auditContrast(doc: Document, win: Window, page: string): ContrastIssue[] {
  const issues: ContrastIssue[] = [];
  const dedupe = new Map<string, ContrastIssue>();
  const els = Array.from(doc.body.querySelectorAll("*")) as HTMLElement[];
  let scanned = 0;

  for (const el of els) {
    if (scanned > 1200) break;
    try {
      // Sadece doğrudan görünür metni olan elemanlar
      const ownText = Array.from(el.childNodes)
        .filter((n) => n.nodeType === 3)
        .map((n) => n.textContent || "")
        .join("")
        .trim();
      if (ownText.length < 2) continue;

      const cs = win.getComputedStyle(el);
      if (cs.visibility === "hidden" || cs.display === "none" || parseFloat(cs.opacity) < 0.1) continue;
      const rect = el.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) continue;

      const fg = parseColor(cs.color);
      if (!fg || fg[3] < 0.3) continue;
      const bg = effectiveBg(el, win);
      if (!bg.opaque) continue; // görsel/degrade üstü → atla

      scanned++;
      const fgRgb: [number, number, number] = [fg[0], fg[1], fg[2]];
      const ratio = contrastRatio(fgRgb, bg.rgb);
      const fontPx = parseFloat(cs.fontSize) || 16;
      const weight = parseInt(cs.fontWeight) || 400;
      const bold = weight >= 700;
      const large = fontPx >= 24 || (fontPx >= 18.66 && bold);
      const required = large ? 3 : 4.5;
      const level: "fail" | "pass" = ratio < required ? "fail" : "pass";
      if (level === "pass") continue;

      const key = rgbToHex(fgRgb) + "|" + rgbToHex(bg.rgb) + "|" + Math.round(fontPx) + "|" + bold;
      if (dedupe.has(key)) continue;
      dedupe.set(key, {
        page,
        sample: ownText.slice(0, 60),
        fg: rgbToHex(fgRgb),
        bg: rgbToHex(bg.rgb),
        ratio: Math.round(ratio * 100) / 100,
        fontPx: Math.round(fontPx),
        bold,
        required,
        level,
      });
    } catch {
      /* yoksay */
    }
  }
  issues.push(...dedupe.values());
  return issues.sort((a, b) => a.ratio - b.ratio);
}

// ── Erişilebilirlik denetimi ────────────────────────────────────────────────

function auditA11y(doc: Document, win: Window, page: string): A11yIssue[] {
  const issues: A11yIssue[] = [];
  const push = (severity: "error" | "warning", rule: string, message: string, detail?: string) =>
    issues.push({ page, severity, rule, message, detail });

  // Görsellerde alt
  const imgs = Array.from(doc.querySelectorAll("img")) as HTMLImageElement[];
  const noAlt = imgs.filter((i) => !i.hasAttribute("alt"));
  if (noAlt.length) {
    push("error", "img-alt", `${noAlt.length} görselde alt metni eksik`, noAlt.slice(0, 4).map((i) => i.src.split("/").pop()).join(", "));
  }

  // Butonlar / linkler erişilebilir isim
  const interactive = Array.from(doc.querySelectorAll("button, a")) as HTMLElement[];
  let emptyCtl = 0;
  for (const el of interactive) {
    const txt = (el.textContent || "").trim();
    const aria = el.getAttribute("aria-label") || el.getAttribute("title");
    const hasImg = el.querySelector("img[alt]:not([alt=''])");
    const hasIcon = el.querySelector("i[class*='ri-'], svg");
    if (!txt && !aria && !hasImg && !(hasIcon && el.getAttribute("aria-label"))) {
      if (!txt && !aria) emptyCtl++;
    }
  }
  if (emptyCtl) push("warning", "control-name", `${emptyCtl} buton/bağlantıda erişilebilir isim yok (yalnızca ikon)`);

  // Başlık hiyerarşisi
  const headings = Array.from(doc.querySelectorAll("h1,h2,h3,h4,h5,h6")) as HTMLElement[];
  const h1s = headings.filter((h) => h.tagName === "H1");
  if (h1s.length === 0) push("error", "h1", "Sayfada hiç <h1> başlık yok");
  else if (h1s.length > 1) push("warning", "h1", `Birden fazla <h1> var (${h1s.length}) — sayfada tek H1 önerilir`);
  let prev = 0;
  for (const h of headings) {
    const lvl = parseInt(h.tagName[1]);
    if (prev && lvl > prev + 1) {
      push("warning", "heading-order", `Başlık seviyesi atlanmış: H${prev} → H${lvl}`, (h.textContent || "").trim().slice(0, 40));
      break;
    }
    prev = lvl;
  }

  // target=_blank rel
  const blanks = (Array.from(doc.querySelectorAll("a[target='_blank']")) as HTMLAnchorElement[]).filter(
    (a) => !(a.rel || "").includes("noopener")
  );
  if (blanks.length) push("warning", "blank-rel", `${blanks.length} dış bağlantıda rel="noopener" yok (güvenlik)`);

  // Form alanları etiketi
  const inputs = Array.from(doc.querySelectorAll("input, textarea, select")) as HTMLElement[];
  let unlabeled = 0;
  for (const el of inputs) {
    const id = el.getAttribute("id");
    const hasLabel = id && doc.querySelector(`label[for='${id}']`);
    const aria = el.getAttribute("aria-label") || el.getAttribute("aria-labelledby");
    const placeholder = el.getAttribute("placeholder");
    if (!hasLabel && !aria && !placeholder) unlabeled++;
  }
  if (unlabeled) push("warning", "form-label", `${unlabeled} form alanında etiket/aria-label yok`);

  // html lang
  if (!doc.documentElement.getAttribute("lang")) {
    push("error", "html-lang", "<html> üzerinde lang özniteliği yok");
  } else if ((doc.documentElement.getAttribute("lang") || "").startsWith("en")) {
    push("warning", "html-lang", `Sayfa Türkçe ama lang="${doc.documentElement.getAttribute("lang")}"`);
  }

  // Çok küçük dokunma hedefleri (mobil) — örnek tarama
  const links = Array.from(doc.querySelectorAll("a, button")) as HTMLElement[];
  let tiny = 0;
  for (const el of links.slice(0, 200)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && (r.height < 24 || r.width < 24)) tiny++;
  }
  if (tiny > 3) push("warning", "tap-target", `${tiny} tıklanabilir öğe 24px'ten küçük (mobil dokunma hedefi)`);

  return issues;
}

// ── SEO / Meta denetimi ─────────────────────────────────────────────────────

function auditSeo(doc: Document, page: string, path: string): SeoResult {
  const head = doc.head;
  const meta = (sel: string) => (head.querySelector(sel) as HTMLMetaElement | null)?.content || "";
  const title = (doc.title || "").trim();
  const description = meta("meta[name='description']");
  return {
    page,
    path,
    title,
    titleLen: title.length,
    description,
    descLen: description.length,
    hasViewport: !!head.querySelector("meta[name='viewport']"),
    lang: doc.documentElement.getAttribute("lang") || "",
    canonical: (head.querySelector("link[rel='canonical']") as HTMLLinkElement | null)?.href || "",
    h1Count: doc.querySelectorAll("h1").length,
    ogTitle: meta("meta[property='og:title']"),
    ogImage: meta("meta[property='og:image']"),
    ogDesc: meta("meta[property='og:description']"),
    favicon: !!head.querySelector("link[rel*='icon']"),
  };
}

// ── Tipografi denetimi ──────────────────────────────────────────────────────

function auditTypography(doc: Document, win: Window): TypographyResult {
  const fonts = new Set<string>();
  const sizes = new Set<number>();
  const weights = new Set<number>();
  const els = Array.from(doc.body.querySelectorAll("*")).slice(0, 1500) as HTMLElement[];
  for (const el of els) {
    try {
      const txt = (el.textContent || "").trim();
      if (!txt) continue;
      const cs = win.getComputedStyle(el);
      const fam = cs.fontFamily.split(",")[0].replace(/["']/g, "").trim();
      if (fam) fonts.add(fam);
      const sz = Math.round(parseFloat(cs.fontSize));
      if (sz) sizes.add(sz);
      const w = parseInt(cs.fontWeight);
      if (w) weights.add(w);
    } catch {
      /* yoksay */
    }
  }
  const headingOutline: { tag: string; text: string; bad: boolean }[] = [];
  const headings = Array.from(doc.querySelectorAll("h1,h2,h3,h4,h5,h6")) as HTMLElement[];
  let prev = 0;
  for (const h of headings) {
    const lvl = parseInt(h.tagName[1]);
    const bad = prev > 0 && lvl > prev + 1;
    headingOutline.push({ tag: h.tagName, text: (h.textContent || "").trim().slice(0, 70), bad });
    prev = lvl;
  }
  return {
    fonts: Array.from(fonts),
    sizes: Array.from(sizes).sort((a, b) => b - a),
    weights: Array.from(weights).sort((a, b) => a - b),
    headingOutline,
  };
}

// ── Ana giriş ───────────────────────────────────────────────────────────────

// Ana pencerede bir görseli yükleyip gerçek durumunu ölç (iframe'den bağımsız)
export function probeImage(url: string, timeout = 9000): Promise<{ ok: boolean; w: number; h: number }> {
  return new Promise((resolve) => {
    let done = false;
    const img = new Image();
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(t);
      resolve({ ok, w: img.naturalWidth || 0, h: img.naturalHeight || 0 });
    };
    const t = setTimeout(() => finish(false), timeout);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    try {
      img.src = url;
    } catch {
      finish(false);
    }
  });
}

export function analyzeDocument(doc: Document, win: Window, page: string, path: string): PageAudit {
  const de = doc.documentElement;
  return {
    page,
    path,
    images: auditImages(doc, win, page),
    contrast: auditContrast(doc, win, page),
    a11y: auditA11y(doc, win, page),
    seo: auditSeo(doc, page, path),
    typography: auditTypography(doc, win),
    scrollW: de.scrollWidth,
    clientW: de.clientWidth,
    overflowX: de.scrollWidth > de.clientWidth + 2,
    domNodes: doc.querySelectorAll("*").length,
  };
}

// ── Skorlama ────────────────────────────────────────────────────────────────

export interface Scores {
  images: number;
  a11y: number;
  contrast: number;
  seo: number;
  overall: number;
}

export function computeScores(audits: PageAudit[], imageList: ImageInfo[]): Scores {
  const allImgs = imageList.filter((i) => !i.isBackground);
  const broken = allImgs.filter((i) => i.broken).length;
  const noAlt = allImgs.filter((i) => !i.hasAltAttr).length;
  const oversized = allImgs.filter((i) => i.oversized).length;
  let imgScore = 100;
  if (allImgs.length) {
    imgScore -= (broken / allImgs.length) * 60;
    imgScore -= (noAlt / allImgs.length) * 25;
    imgScore -= (oversized / allImgs.length) * 15;
  }

  const contrastAll = audits.flatMap((a) => a.contrast);
  const contrastFails = contrastAll.filter((c) => c.level === "fail").length;
  const contrastScore = Math.max(0, 100 - contrastFails * 8);

  const a11yAll = audits.flatMap((a) => a.a11y);
  const a11yErr = a11yAll.filter((i) => i.severity === "error").length;
  const a11yWarn = a11yAll.filter((i) => i.severity === "warning").length;
  const a11yScore = Math.max(0, 100 - a11yErr * 15 - a11yWarn * 6);

  let seoTotal = 0;
  let seoPass = 0;
  for (const a of audits) {
    const s = a.seo;
    const checks = [
      s.titleLen >= 10 && s.titleLen <= 65,
      s.descLen >= 50 && s.descLen <= 165,
      s.hasViewport,
      !!s.lang && !s.lang.startsWith("en"),
      s.h1Count === 1,
      !!s.ogTitle,
      !!s.ogImage,
      s.favicon,
    ];
    seoTotal += checks.length;
    seoPass += checks.filter(Boolean).length;
  }
  const seoScore = seoTotal ? Math.round((seoPass / seoTotal) * 100) : 0;

  const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
  const images = clamp(imgScore);
  const a11y = clamp(a11yScore);
  const contrast = clamp(contrastScore);
  const seo = clamp(seoScore);
  return {
    images,
    a11y,
    contrast,
    seo,
    overall: clamp((images + a11y + contrast + seo) / 4),
  };
}
